import { VehicleImageWithMeta } from '@/catalog/types';
import { createImageUrl } from '@/catalog/utils/pdp/create-image-url';
import Spacer from '@/common/components/spacer';
import { Button } from '@/shadcn-ui/button';
import { motion } from 'motion/react';
import { ReactNode } from 'react';
import HeroMissingImage from './hero-missing-image';
import ImagePreviewHeroGradient from './image-preview-hero-gradient';
import { findImageBySequence } from '@/catalog/utils/pdp/find-image-by-sequence';
import { findImagesByCategory } from '@/catalog/utils/pdp/find-images-by-category';
import { findFirstImageByCategory } from '@/catalog/utils/pdp/find-first-image-by-category';
import GalleryPerspective from '@/icons/gallery-perspective';
import Gallery from '@/icons/gallery';

interface ImageCarouselGalleryProps {
  interiorImages: VehicleImageWithMeta[];
  exteriorImages: VehicleImageWithMeta[];
  heroImage?: VehicleImageWithMeta;
  cdnUrl: string;
  interstitialContent?: ReactNode;
  /** Image passed to preview, otherwise show default if image undefined */
  onClickDelegate: (image?: string) => void;
}

export default function ImagePreviewShort({
  interiorImages,
  exteriorImages,
  heroImage,
  cdnUrl,
  interstitialContent,
  onClickDelegate,
}: ImageCarouselGalleryProps) {
  const heroImageUrl = heroImage ? createImageUrl(cdnUrl, heroImage) : undefined;
  const exteriorSide = findImageBySequence(
    exteriorImages,
    2, // Get the third image in the exterior images, which should typically be side view
    cdnUrl
  );

  // Interior shots are a bit irregular, we want to show interior 4 first. The third image is for the 360 preview. The other two are backup thumbnails.
  const interiorWheelFocusShot = findImageBySequence(interiorImages, 3, cdnUrl);
  const interiorImagesUrls = findImagesByCategory(interiorImages, 'Interior', {
    limit: 3,
    cdnUrl,
  });

  const interior360 = findFirstImageByCategory(interiorImages, '360int', {
    cdnUrl,
  });

  return (
    <>
      <div className="relative aspect-[4/3] w-full">
        {heroImageUrl ? (
          <Button
            variant="clear"
            onClick={() => onClickDelegate()}
            className="relative h-fit overflow-hidden"
          >
            <motion.img
              alt="Vehicle exterior front"
              src={heroImageUrl}
              fetchPriority="high"
              loading="eager"
              className="z-0 h-full w-full object-cover"
              initial={{ scale: 1.5 }}
              whileTap={{ scale: 1.485 }}
              transition={{ duration: 0.1 }}
            />
            <ImagePreviewHeroGradient />
          </Button>
        ) : (
          <HeroMissingImage />
        )}
      </div>
      {interstitialContent}
      <Spacer variant="height24" />
      <div className="flex h-full flex-row justify-start gap-[12px] px-[24px] md:px-0">
        {exteriorSide ? (
          <Button
            variant="clear"
            onClick={() => onClickDelegate(exteriorSide)}
            className="h-full w-1/4"
          >
            <motion.img
              src={exteriorSide}
              alt="Vehicle exterior side"
              className="aspect-[4/3] w-full rounded-[16px] object-cover shadow-shadow10 hover:shadow-shadow20"
              fetchPriority="high"
              loading="eager"
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.1 }}
            />
          </Button>
        ) : null}

        {interior360 ? (
          <Button
            variant="clear"
            onClick={() => onClickDelegate(interior360)}
            className="h-full w-1/4 rounded-[16px] object-cover"
          >
            <motion.div
              className="group relative flex aspect-[4/3] h-full w-full flex-col items-center justify-center overflow-hidden rounded-[16px]"
              whileTap={{ scale: 0.98 }}
              transition={{ duration: 0.1 }}
            >
              <img
                src={
                  // Preserve original preference order: index 2, then 1, then 0, then fallback to 360 image if it exists. 360 preview is not preferred but better than not showing anything.
                  interiorImagesUrls[2] ??
                  interiorImagesUrls[1] ??
                  interiorImagesUrls[0] ??
                  interior360
                }
                alt="Vehicle interior 360 preview"
                fetchPriority="high"
                loading="eager"
                className="absolute inset-0 z-0 h-full w-full blur-[4px] transition-all group-hover:blur-[2px]"
              />
              <p className="z-1 rounded-[32px] bg-flexcar-neutral100/90 px-[12px] py-[8px] font-sans-tight text-[10px] font-bold leading-[150%] tracking-[0.8px] text-flexcar-neutral20 group-hover:bg-flexcar-neutral100 group-hover:text-flexcar-neutral1">
                <span className="whitespace-nowrap">360º</span>{' '}
                <span className="hidden whitespace-nowrap min-[1300px]:inline">
                  INTERIOR
                </span>
              </p>
            </motion.div>
          </Button>
        ) : null}

        {interiorWheelFocusShot ? (
          <Button
            variant="clear"
            onClick={() => onClickDelegate(interiorWheelFocusShot)}
            className="h-full w-1/4"
          >
            <motion.img
              src={interiorWheelFocusShot}
              alt="Vehicle driving wheel focus shot"
              className="aspect-[4/3] w-full rounded-[16px] object-cover shadow-shadow10 hover:shadow-shadow20"
              fetchPriority="high"
              loading="eager"
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.1 }}
            />
          </Button>
        ) : null}

        {/* If we are missing the 360 thumbnail, show the interior wheel focus shot thumbnail then show the first interior thumbnail */}
        {!interior360 && interiorImagesUrls[0] ? (
          <Button
            variant="clear"
            onClick={() => onClickDelegate(interiorImagesUrls[0])}
            className="h-full w-1/4"
          >
            <motion.img
              src={interiorImagesUrls[0]}
              alt="Vehicle interior first"
              className="aspect-[4/3] w-full rounded-[16px] object-cover shadow-shadow10 hover:shadow-shadow20"
              fetchPriority="high"
              loading="eager"
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.1 }}
            />
          </Button>
        ) : null}

        {interiorImages?.length > 0 || exteriorImages.length > 0 ? (
          <Button
            variant="clear"
            className="group flex aspect-[4/3] h-full w-1/4 flex-col items-center justify-center gap-[4px] rounded-[16px] border-[1px] border-solid border-flexcar-neutral80 bg-flexcar-neutral100 p-[8px] transition-all sm:flex-row"
            onClick={() => onClickDelegate()}
          >
            <Gallery className="hidden h-[24px] w-[27px] flex-shrink-0 group-hover:text-flexcar-blue40 sm:block" />
            <GalleryPerspective
              className="block flex-shrink-0 sm:hidden"
              hoverFillPrimary="group-hover:fill-flexcar-blue40"
            />
            <p className="group-hover footer text-flexcar-neutral10 sm:button1 group-hover:text-flexcar-blue40">
              View all
            </p>
          </Button>
        ) : null}
      </div>
    </>
  );
}
